import { useMemo } from "react";
import { Users } from "lucide-react";
import { useRealtime } from "@/contexts/RealtimeContext";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Shared presence strip — surfaces which other admins are currently on
 * the same property. Consumers pass `propertyId`; the strip reads
 * `presence[propertyId]` from RealtimeContext (already auto-subscribed
 * on connect), dedupes by user_id, and excludes the logged-in admin.
 *
 * Optional `label` prop overrides the default "N admins viewing" text
 * so a specific screen (e.g. "on this record") can override the copy.
 * Optional `roleFilter` limits to a specific role (default: 'admin').
 */
export function PresenceStrip({ propertyId, roleFilter = "admin", label, testId = "presence-strip" }) {
  const { presence } = useRealtime();
  const { user: me } = useAuth();

  const others = useMemo(() => {
    if (!propertyId) return [];
    const list = presence?.[propertyId] || [];
    const seen = new Set();
    return list.filter((p) => {
      if (roleFilter && p.role !== roleFilter) return false;
      if (p.user_id === me?.id) return false;
      if (seen.has(p.user_id)) return false;
      seen.add(p.user_id);
      return true;
    });
  }, [presence, propertyId, me?.id, roleFilter]);

  if (others.length === 0) return null;

  const noun = roleFilter === "admin"
    ? (others.length === 1 ? "admin" : "admins")
    : (roleFilter || "user");

  return (
    <div className="inline-flex items-center gap-1.5" data-testid={testId}>
      <Users className="h-3 w-3 text-emerald-400" />
      <div className="flex -space-x-1.5">
        {others.slice(0, 4).map((p) => {
          const initials = (p.name || "?")
            .split(" ")
            .map((s) => s[0])
            .filter(Boolean)
            .slice(0, 2)
            .join("")
            .toUpperCase();
          return (
            <div
              key={p.user_id}
              className="relative w-6 h-6 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-[10px] font-semibold text-emerald-200"
              title={`${p.name} is also viewing`}
              data-testid={`${testId}-${p.user_id}`}
            >
              {initials}
              <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 border border-card"></span>
            </div>
          );
        })}
        {others.length > 4 && (
          <div className="w-6 h-6 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-[10px] text-zinc-400">
            +{others.length - 4}
          </div>
        )}
      </div>
      <span className="text-[10px] text-emerald-300 ml-1">
        {label || `${others.length} ${noun} viewing`}
      </span>
    </div>
  );
}

export default PresenceStrip;
